import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Ban, ChevronLeft, ChevronRight, Gavel } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, EmptyState, Skeleton, SourceText } from "@ncp/ui";
import { stageOf } from "../case/process/phases";

interface OpenFile {
  caseId: string; caseNumber: string; title: string; titleAr?: string | null; type: string; state: string; status?: string | null; stateLabel?: string | null; stateLabelAr?: string | null; enteredAt?: string | null;
  hold?: { reason: string; reasonAr?: string | null; by: string; at: string } | null; decisionId?: string | null; decisionNumber?: string | null;
  reasons: Array<{ code: string; en: string; ar: string; blocking: boolean }>;
}

/** One page of the open files, with how many are open in all and how many are marked not signable. */
interface OpenPage { items: OpenFile[]; total: number; marked: number; page: number; pageSize: number }

const PAGE_SIZE = 50;

/** The open files, a page at a time: every open file is counted, however many there are. */
export function useOpenDecisionFiles(page = 1) {
  const api = useApi();
  return useQuery({ queryKey: ["decisions-open", page], queryFn: () => api.get<OpenPage>("/api/decisions/open", { page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData });
}

/**
 * The archive of files at a decision stage (scenario 6.35): every file awaiting its decision and every file whose decision document is marked
 * not signable, with why each remains open – the marking with its reason, officer and time, the stage's condition, the registers, the signatory awaited.
 */
export function OpenDecisionFiles() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const files = useOpenDecisionFiles(page);
  const ar = locale === "ar";
  if (files.isLoading) return <Skeleton className="h-64" />;
  const items = files.data?.items ?? [];
  const total = files.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = (page - 1) * PAGE_SIZE + 1;
  const pager = pages > 1 ? (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>{t("backoffice:decisionControls.open.range", { from: formatNumber(first, locale), to: formatNumber(first + items.length - 1, locale), total: formatNumber(total, locale) })}</span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon-sm" disabled={page <= 1 || files.isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label={t("backoffice:decisionControls.open.previous")}><ChevronLeft className="rtl:rotate-180" /></Button>
        <span className="tabular">{t("backoffice:decisionControls.open.pageOf", { page: formatNumber(page, locale), pages: formatNumber(pages, locale) })}</span>
        <Button variant="ghost" size="icon-sm" disabled={page >= pages || files.isFetching} onClick={() => setPage((p) => Math.min(pages, p + 1))} aria-label={t("backoffice:decisionControls.open.next")}><ChevronRight className="rtl:rotate-180" /></Button>
      </div>
    </div>
  ) : null;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("backoffice:decisionControls.open.hint")}</p>
      {pager}
      {items.length === 0 ? <EmptyState icon={Gavel} title={t("backoffice:decisionControls.open.empty")} /> : (
        <div className="grid gap-3 lg:grid-cols-2">
          {items.map((f) => (
            <Card key={f.caseId} interactive className="cursor-pointer p-4" onClick={() => navigate(`/cases/${f.caseId}?tab=decision`)}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-aegold-800">{f.caseNumber}</span>
                <Badge variant="outline" size="sm">{t(`caseTypes.${f.type}`, { defaultValue: f.type })}</Badge>
                {/* The stage in the six words of every list (finding 7.5); the step within it follows. */}
                <span className="text-xs font-medium text-sand-800">{t(`backoffice:workspace.phases.${stageOf(f.type, f.state, f.status)}`)}</span>
                {f.hold ? <Badge variant="danger" size="sm"><Ban /> {t("backoffice:decisionControls.open.marked")}</Badge> : null}
                {f.decisionNumber ? <Badge variant="outline" size="sm">{t("backoffice:decisionControls.open.issued", { number: f.decisionNumber })}</Badge> : null}
              </div>
              <p className="mt-1.5 line-clamp-2 text-sm font-medium"><SourceText text={ar && f.titleAr ? f.titleAr : f.title} /></p>
              <p className="mt-0.5 text-xs text-muted-foreground">{(ar ? f.stateLabelAr : f.stateLabel) ?? f.state}</p>
              {f.enteredAt ? <p className="mt-0.5 text-xs text-muted-foreground">{t("backoffice:decisionControls.open.since", { date: formatDate(f.enteredAt, locale) })}</p> : null}
              <ul className="mt-2 space-y-1 text-xs">
                {f.reasons.map((r) => <li key={r.code + r.en} className={r.blocking ? "text-danger-700" : "text-muted-foreground"}>{ar ? r.ar : r.en}</li>)}
              </ul>
            </Card>
          ))}
        </div>
      )}
      {pager}
    </div>
  );
}
